<script setup lang="ts">
// ================================
// IMPORTS
// ================================
import { computed, ref, watch } from "vue"
import { useForm } from "vee-validate"
import { toTypedSchema } from "@vee-validate/yup"
import * as yup from "yup"
import { useI18n } from "vue-i18n"
import { storeToRefs } from "pinia"
import type { Building } from "@types"
import { useBuildingStore } from "@core/stores/building-v2.store"

// ================================
// PROPS
// ================================
const props = defineProps<{
    readonly: boolean
    /** Bâtiment en consultation seule : champs désactivés (pas `readonly`). */
    disabled?: boolean
}>()

const getFieldReadonly = computed(() => props.readonly && !props.disabled)
const getFieldDisabled = computed(() => Boolean(props.disabled))

// ================================
// STORES
// ================================
const buildingStore = useBuildingStore()
const { building } = storeToRefs(buildingStore)

// ================================
// INTERNATIONALIZATION
// ================================
const { t } = useI18n({
    messages: {
        fr: {
            title: "Informations du logement/local",
            sectionGeneral: "Informations générales",
            sectionMeasures: "Mesures",
            sectionDpe: "DPE",
            occupants: "Nombre de personnes",
            residence: "Résidence",
            pieces: "Nombre de pièces",
            fenetres: "Nombre de fenêtres",
            surface: "Surface habitable (m²)",
            save: "Sauvegarder",
            cancel: "Annuler",
            validation: {
                number: "Saisissez un nombre valide.",
                occupantsMin: "Au moins une personne est requise.",
                occupantsRequired: "Le nombre de personnes est obligatoire.",
            },
        },
    },
})

const residenceOptions = [
    { label: "Principale", value: "principale" },
    { label: "Secondaire", value: "secondaire" },
]

type LogementDpe = NonNullable<Building["infos"]["logement"]>["dpe"]

// ================================
// FORM
// ================================
const getValidationSchema = computed(() =>
    toTypedSchema(
        yup.object({
            nombreOccupants: yup
                .number()
                .typeError(t("validation.number"))
                .min(1, t("validation.occupantsMin"))
                .required(t("validation.occupantsRequired")),
            residence: yup.string().nullable().optional(),
            nombrePieces: yup.number().min(0).nullable(),
            nombreFenetres: yup.number().min(0).nullable(),
            surfaceHabitable: yup.number().min(0).nullable(),
        })
    )
)

function getPrimeInvalidFieldProps(state: { errors: string[] }) {
    return {
        props: {
            invalid: state.errors.length > 0,
        },
    }
}

const { handleSubmit, defineField, setFieldValue, errors } = useForm({
    validationSchema: getValidationSchema,
    initialValues: {
        nombreOccupants: 1,
        residence: null as string | null,
        nombrePieces: null as number | null,
        nombreFenetres: null as number | null,
        surfaceHabitable: null as number | null,
    },
})

const [nombreOccupants, nombreOccupantsAttrs] = defineField(
    "nombreOccupants",
    getPrimeInvalidFieldProps
)
const [residence, residenceAttrs] = defineField(
    "residence",
    getPrimeInvalidFieldProps
)
const [nombrePieces] = defineField("nombrePieces")
const [nombreFenetres] = defineField("nombreFenetres")
const [surfaceHabitable] = defineField("surfaceHabitable")

const equipementRef = ref<InstanceType<
    typeof BuildingInfoGeneralEquipement
> | null>(null)

const loading = ref(false)
const errorMsg = ref("")

const dpe = ref<LogementDpe>({
    energie: { value: null, date: null },
    ges: { value: null, date: null },
})

function syncFromBuilding(): void {
    const b = building.value
    const log = b?.infos?.logement
    setFieldValue("nombreOccupants", log?.nombre_occupants ?? 1)
    setFieldValue("residence", log?.residence ?? null)
    setFieldValue("nombrePieces", log?.nombre_pieces ?? null)
    setFieldValue("nombreFenetres", log?.nombre_fenetres ?? null)
    setFieldValue("surfaceHabitable", log?.surface?.habitable ?? null)
    dpe.value = {
        energie: {
            value: log?.dpe?.energie?.value ?? null,
            date: log?.dpe?.energie?.date ?? null,
        },
        ges: {
            value: log?.dpe?.ges?.value ?? null,
            date: log?.dpe?.ges?.date ?? null,
        },
    }
}

watch(building, syncFromBuilding, { immediate: true, deep: true })

function resolveBuildingMongoId(b: Building): string {
    return b._id ?? b._id_building
}

function isDpeEqualToLogement(
    local: LogementDpe,
    fromLogement: Building["infos"]["logement"] | undefined
): boolean {
    const normDate = (x: unknown): string | null => {
        if (x == null) return null
        if (x instanceof Date) return x.toISOString()
        return String(x)
    }
    const pack = (d: LogementDpe | undefined) =>
        JSON.stringify({
            eV: d?.energie?.value ?? null,
            eD: normDate(d?.energie?.date),
            gV: d?.ges?.value ?? null,
            gD: normDate(d?.ges?.date),
        })
    return pack(local) === pack(fromLogement?.dpe)
}

const getHasChanged = computed(() => {
    const b = building.value
    if (!b) return false
    const log = b.infos?.logement
    const n = (v: number | null | undefined) => v ?? null
    if (
        nombreOccupants.value !== (log?.nombre_occupants ?? 1) ||
        residence.value !== (log?.residence ?? null) ||
        n(nombrePieces.value) !== n(log?.nombre_pieces) ||
        n(nombreFenetres.value) !== n(log?.nombre_fenetres) ||
        n(surfaceHabitable.value) !== n(log?.surface?.habitable)
    ) {
        return true
    }
    if (!isDpeEqualToLogement(dpe.value, log)) {
        return true
    }
    return equipementRef.value?.hasEnergyUseChanged() ?? false
})

const onSubmit = handleSubmit(async (values) => {
    const b = building.value
    if (!b || props.readonly || props.disabled) return
    try {
        loading.value = true
        errorMsg.value = ""
        const id = resolveBuildingMongoId(b)
        await buildingStore.updateBuildingById(
            id,
            {
                "infos.logement.nombre_occupants": values.nombreOccupants,
                "infos.logement.residence": values.residence,
                "infos.logement.nombre_pieces":
                    values.nombrePieces ?? undefined,
                "infos.logement.nombre_fenetres":
                    values.nombreFenetres ?? undefined,
                "infos.logement.surface.habitable":
                    values.surfaceHabitable ?? undefined,
                "infos.logement.dpe": dpe.value,
                "infos.logement.energyuse":
                    equipementRef.value?.getEnergyUsePayload() ?? [],
            } as Record<string, unknown>,
            { silent: true }
        )
    } catch (e) {
        errorMsg.value = (e as Error).message
    } finally {
        loading.value = false
    }
})

function onCancel(): void {
    syncFromBuilding()
    equipementRef.value?.syncFromStore()
}
</script>

<template>
    <BaseTitledCard class="building-info-general-house w-full shadow-sm">
        <template #header>
            {{ t("title") }}
        </template>
        <template #content>
            <form
                class="space-y-8 flex flex-col gap-8"
                @submit.prevent="onSubmit"
            >
                <fieldset
                    class="grid grid-cols-1 md:grid-cols-2 gap-4 border-0 p-0 m-0"
                >
                    <legend
                        class="md:col-span-2 text-base font-semibold text-surface-900 mb-4"
                    >
                        {{ t("sectionGeneral") }}
                    </legend>
                    <BaseInputWrapper
                        :label="t('occupants')"
                        input-id="building-info-general-house-foyer-occupants"
                        :error="errors.nombreOccupants ?? ''"
                        unfloat
                    >
                        <BaseInputNumber
                            v-model="nombreOccupants"
                            v-bind="nombreOccupantsAttrs"
                            input-id="building-info-general-house-foyer-occupants"
                            class="w-full"
                            :min="1"
                            :show-buttons="!getFieldDisabled"
                            button-layout="horizontal"
                            decrement-button-icon="pi pi-minus"
                            increment-button-icon="pi pi-plus"
                            :readonly="getFieldReadonly"
                            :disabled="getFieldDisabled"
                            :style="{
                                '--p-inputgroup-addon-border-radius': '0',
                            }"
                        />
                    </BaseInputWrapper>
                    <BaseInputWrapper
                        :label="t('residence')"
                        input-id="building-info-general-house-foyer-residence"
                        :error="errors.residence ?? ''"
                        unfloat
                    >
                        <BaseSelect
                            v-model="residence"
                            v-bind="residenceAttrs"
                            input-id="building-info-general-house-foyer-residence"
                            :options="residenceOptions"
                            option-label="label"
                            option-value="value"
                            class="w-full"
                            :disabled="readonly || disabled"
                            show-clear
                            placeholder="—"
                        />
                    </BaseInputWrapper>
                    <BaseInputWrapper
                        :label="t('pieces')"
                        input-id="building-info-general-house-pieces"
                        unfloat
                    >
                        <BaseInputNumber
                            v-model="nombrePieces"
                            input-id="building-info-general-house-pieces"
                            class="w-full"
                            :min="0"
                            :show-buttons="!getFieldDisabled"
                            button-layout="horizontal"
                            decrement-button-icon="pi pi-minus"
                            increment-button-icon="pi pi-plus"
                            :readonly="getFieldReadonly"
                            :disabled="getFieldDisabled"
                            :style="{
                                '--p-inputgroup-addon-border-radius': '0',
                            }"
                        />
                    </BaseInputWrapper>
                    <BaseInputWrapper
                        :label="t('fenetres')"
                        input-id="building-info-general-house-fenetres"
                        unfloat
                    >
                        <BaseInputNumber
                            v-model="nombreFenetres"
                            input-id="building-info-general-house-fenetres"
                            class="w-full"
                            :min="0"
                            :show-buttons="!getFieldDisabled"
                            button-layout="horizontal"
                            decrement-button-icon="pi pi-minus"
                            increment-button-icon="pi pi-plus"
                            :readonly="getFieldReadonly"
                            :disabled="getFieldDisabled"
                            :style="{
                                '--p-inputgroup-addon-border-radius': '0',
                            }"
                        />
                    </BaseInputWrapper>
                </fieldset>

                <fieldset
                    class="grid grid-cols-1 md:grid-cols-2 gap-4 border-0 p-0 m-0"
                >
                    <legend
                        class="md:col-span-2 text-base font-semibold text-surface-900 mb-4"
                    >
                        {{ t("sectionMeasures") }}
                    </legend>
                    <BaseInputWrapper
                        class="md:col-span-2"
                        :label="t('surface')"
                        input-id="building-info-general-house-surface"
                        unfloat
                    >
                        <BaseInputNumber
                            v-model="surfaceHabitable"
                            input-id="building-info-general-house-surface"
                            class="w-full max-w-md"
                            :min="0"
                            :max-fraction-digits="2"
                            :readonly="getFieldReadonly"
                            :disabled="getFieldDisabled"
                        />
                    </BaseInputWrapper>
                </fieldset>

                <fieldset class="border-0 p-0 m-0">
                    <legend
                        class="text-base font-semibold text-surface-900 mb-4"
                    >
                        {{ t("sectionDpe") }}
                    </legend>
                    <BuildingInfoGeneralDpe
                        v-model="dpe"
                        :readonly="readonly"
                        :disabled="disabled"
                        display-hint
                    />
                </fieldset>

                <BuildingInfoGeneralEquipement
                    ref="equipementRef"
                    scope="logement"
                    :readonly="readonly"
                    :disabled="disabled"
                />

                <div
                    v-if="getHasChanged && !readonly && !disabled"
                    class="flex flex-wrap justify-center gap-3"
                >
                    <BaseButton
                        type="button"
                        severity="secondary"
                        :label="t('cancel')"
                        @click="onCancel"
                    >
                    </BaseButton>
                    <BaseButton
                        type="submit"
                        :loading="loading"
                        :label="t('save')"
                    >
                    </BaseButton>
                </div>
                <BaseMessage v-if="errorMsg" severity="error" :closable="false">
                    {{ errorMsg }}
                </BaseMessage>
            </form>
        </template>
    </BaseTitledCard>
</template>
